import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, RefreshCw, PlusCircle, Edit3, Sparkles, Brain, Target } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';

/**
 * Modal presenting three assessment options to students:
 * 1. Regenerate (reprocess existing answers)
 * 2. Retake (start fresh)
 * 3. Revise (edit existing answers)
 */
const AssessmentOptionsModal = ({ isOpen, onClose, className = '' }) => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const [hasExistingAssessment, setHasExistingAssessment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [latestAssessmentId, setLatestAssessmentId] = useState(null);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Check if user has any existing assessments
  useEffect(() => {
    const checkExistingAssessments = async () => {
      if (!currentUser?.id) return;

      try {
        const { data, error } = await supabase
          .from('assessment_responses')
          .select('id, created_at')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error checking assessments:', error);
          setHasExistingAssessment(false);
        } else {
          setHasExistingAssessment(data && data.length > 0);
          if (data && data.length > 0) {
            setLatestAssessmentId(data[0].id);
          }
        }
      } catch (error) {
        console.error('Error checking assessments:', error);
        setHasExistingAssessment(false);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      checkExistingAssessments();
    }
  }, [currentUser?.id, isOpen]);

  // Handle regenerate option (reprocess existing answers)
  const handleRegenerate = () => {
    onClose();
    // Navigate to existing assessment results where ReprocessAssessmentButton will be shown
    navigate('/college/strategic-assessment-results');
  };

  // Handle retake option (fresh start)
  const handleRetake = () => {
    onClose();
    // Clear any cached assessment data and navigate to fresh form
    navigate('/college/strategic-assessment', { 
      replace: true,
      state: { freshStart: true } 
    });
  };

  // Handle revise option (edit existing answers)
  const handleRevise = () => {
    onClose();
    // Navigate to assessment form in revision mode
    navigate('/college/strategic-assessment', { 
      state: { 
        revisionMode: true, 
        assessmentId: latestAssessmentId 
      } 
    });
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${className}`}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900 bg-opacity-50 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="relative p-6 pb-4">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-stone-100 transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-azure-500 to-violet-600 rounded-full flex items-center justify-center">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Strategic Assessment Options
              </h2>
              <p className="text-slate-600">
                {hasExistingAssessment 
                  ? "You have a previous assessment. Choose how you'd like to proceed:"
                  : "Choose how you'd like to create your strategic profile:"
                }
              </p>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="px-6 pb-6">
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-azure-500" />
              </div>
            </div>
          )}

          {/* Options */}
          {!loading && (
            <div className="px-6 pb-6 space-y-4">
              {/* Option 1: Regenerate (only show if has existing assessment) */}
              {hasExistingAssessment && (
                <button
                  onClick={handleRegenerate}
                  className="w-full p-6 border-2 border-azure-200 rounded-xl hover:border-azure-300 hover:bg-azure-50 transition-all group text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-azure-100 rounded-lg flex items-center justify-center group-hover:bg-azure-200 transition-colors">
                      <RefreshCw className="w-6 h-6 text-azure-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          🔄 Regenerate Assessment
                        </h3>
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                          FASTEST
                        </span>
                      </div>
                      <p className="text-slate-600 mb-3">
                        Get enhanced insights from your existing answers using improved AI analysis. No need to answer questions again.
                      </p>
                      <div className="text-sm text-stone-500">
                        ✓ Uses your previous responses • ✓ Version B strategic insights • ✓ Ready in seconds
                      </div>
                    </div>
                  </div>
                </button>
              )}

              {/* Option 2: Retake */}
              <button
                onClick={handleRetake}
                className="w-full p-6 border-2 border-stone-200 rounded-xl hover:border-stone-300 hover:bg-stone-50 transition-all group text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center group-hover:bg-stone-200 transition-colors">
                    <PlusCircle className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">
                        🆕 Retake Assessment
                      </h3>
                      {!hasExistingAssessment && (
                        <span className="px-2 py-1 bg-azure-100 text-azure-700 text-xs font-medium rounded-full">
                          START HERE
                        </span>
                      )}
                    </div>
                    <p className="text-slate-600 mb-3">
                      Start completely fresh with a blank assessment. Perfect if you want to approach the questions differently.
                    </p>
                    <div className="text-sm text-stone-500">
                      ✓ Clean slate approach • ✓ New perspective • ✓ Takes ~15 minutes
                    </div>
                  </div>
                </div>
              </button>

              {/* Option 3: Revise (only show if has existing assessment) */}
              {hasExistingAssessment && (
                <button
                  onClick={handleRevise}
                  className="w-full p-6 border-2 border-violet-200 rounded-xl hover:border-violet-300 hover:bg-violet-50 transition-all group text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                      <Edit3 className="w-6 h-6 text-violet-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          ✏️ Revise Your Answers
                        </h3>
                        <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">
                          BEST RESULTS
                        </span>
                      </div>
                      <p className="text-slate-600 mb-3">
                        Edit your previous answers to add more details and context. This typically yields the most insightful results.
                      </p>
                      <div className="text-sm text-stone-500">
                        ✓ Pre-filled with your answers • ✓ Add more detail • ✓ Usually gets better AI insights
                      </div>
                    </div>
                  </div>
                </button>
              )}

              {/* First-time user guidance */}
              {!hasExistingAssessment && (
                <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 mt-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-azure-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-azure-900 mb-1">First time here?</h4>
                      <p className="text-sm text-azure-700">
                        Start with "Retake Assessment" to create your first strategic profile. You can always regenerate or revise later!
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentOptionsModal;